import { Box } from "@mui/material";
import { type FunctionComponent, type ReactElement } from "react";
import { FEATURE_PANEL_WIDTH, TITLE_HEIGHT } from "../constants";
import type { ExportImageType } from "../features/GraphExporter";
import type FeatureNodeType from "../features/FeatureNodeType";
import type FeatureOperation from "../features/FeatureOperation";
import type SelectedFeature from "../features/SelectedFeature";
import type GraphContainerState from "../states/GraphContainerState";
import AddNodesPanel from "./AddNodesPanel";
import SaveLoadPanel from "./SaveLoadPanel";
import TutorialPanel from "./TutorialPanel";

interface FeaturePanelProps {
  feature: SelectedFeature;
  featureOperations: FeatureOperation[];
  operationIdsAddedAtLeastOnce: ReadonlySet<string>;
  isDarkMode: boolean;
  hasNodes: boolean;
  isExporting: boolean;
  onAddNode: (featureNodeType: FeatureNodeType) => void;
  onAddOperation: () => void;
  onEditOperation: (updatedOperation: FeatureOperation) => void;
  onDeleteOperation: (operationId: string) => void;
  onSave: () => GraphContainerState;
  onLoad: (graphContainerState: GraphContainerState) => void;
  onNewGraph: () => void;
  onExport: (imageType: ExportImageType) => void;
}

const FeaturePanel: FunctionComponent<FeaturePanelProps> = ({
  feature,
  featureOperations,
  operationIdsAddedAtLeastOnce,
  isDarkMode,
  hasNodes,
  isExporting,
  onAddNode,
  onAddOperation,
  onEditOperation,
  onDeleteOperation,
  onSave,
  onLoad,
  onNewGraph,
  onExport,
}) => {
  const renderSelectedPanel = (): ReactElement => {
    switch (feature) {
      case "add-nodes":
        return (
          <AddNodesPanel
            featureOperations={featureOperations}
            operationIdsAddedAtLeastOnce={operationIdsAddedAtLeastOnce}
            isDarkMode={isDarkMode}
            onAddNode={onAddNode}
            onAddOperation={onAddOperation}
            onEditOperation={onEditOperation}
            onDeleteOperation={onDeleteOperation}
          />
        );
      case "save-load":
        return (
          <SaveLoadPanel
            onSave={onSave}
            onLoad={onLoad}
            hasNodes={hasNodes}
            onNewGraph={onNewGraph}
            onExport={onExport}
            isExporting={isExporting}
          />
        );
      case "tutorial":
        return <TutorialPanel />;
    }
  };
  return (
    <Box
      width={FEATURE_PANEL_WIDTH}
      height={`calc(100vh - ${TITLE_HEIGHT}px)`}
      overflow="auto"
    >
      {renderSelectedPanel()}
    </Box>
  );
};

export default FeaturePanel;
